import { Link } from "react-router-dom";
import Seo from "@/components/site/Seo";
import { motion } from "framer-motion";
import Reveal from "@/components/site/Reveal";
import { Check, ArrowUpRight, Users, Package, Sparkles, BookOpen, Calendar, Network, Library, Compass } from "lucide-react";

const roster = [
  "Alibaba", "TCL", "Pizza Hut", "Upwork", "Fiverr", "Kleo", "CAD-IT", "myx",
  "Alibaba", "TCL", "Pizza Hut", "Upwork", "Fiverr", "Kleo", "CAD-IT", "myx",
];

const services = [
  {
    tag: "01 · Foundation",
    title: "Brand Building",
    body: "We design the identity, the meaning, and the memory — the things that make a brand instantly recognizable.",
    items: ["Logo design & visual identity", "Brand strategy & positioning", "Messaging & voice", "Guidelines & launch kits"],
    mockup: "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=1200",
  },
  {
    tag: "02 · Everyday & moments",
    title: "Brand Experience",
    body: "We design the moments your brand meets people — the campaigns, the content, the creative that shows up everywhere.",
    items: ["Ad creative & social", "Motion & video", "Landing pages", "Campaign systems"],
    mockup: "https://images.unsplash.com/photo-1618556450991-2f1af64e8191?w=1200",
  },
  {
    tag: "03 · Product & web",
    title: "Digital Experience",
    body: "We design where your brand becomes something people use — websites and products built to carry the brand.",
    items: ["UI/UX design", "Websites", "Design systems", "Digital products"],
    mockup: "https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?w=1200",
  },
];

const glimpse = [
  { url: "/work/kleo-01.png", label: "Brand Identity · Motion", span: "col-span-6 md:col-span-3", ratio: "aspect-[3/4]" },
  { url: "/work/neurova-01.jpg", label: "Ad Creative", span: "col-span-6 md:col-span-6", ratio: "aspect-[16/10]" },
  { url: "/work/flaren-01.jpg", label: "Brand Identity", span: "col-span-6 md:col-span-3", ratio: "aspect-[3/4]" },
  { url: "/work/twist-01.jpg", label: "Ad Creative", span: "col-span-6 md:col-span-4", ratio: "aspect-[4/3]" },
  { url: "/work/haven-01.jpg", label: "Web Design", span: "col-span-6 md:col-span-4", ratio: "aspect-[4/3]" },
  { url: "/work/mobile-app-ux-01.jpg", label: "App Design", span: "col-span-6 md:col-span-4", ratio: "aspect-[4/3]" },
];

const membershipFeatures = [
  { icon: Users, title: "Dedicated senior team", desc: "A reserved design team, five hours every business day. Same faces, all year." },
  { icon: Package, title: "Big-project budget", desc: "Held for rebrands, launches, sites. Never expires while you're a member." },
  { icon: Sparkles, title: "Design leader sessions", desc: "Six live sessions a year with world-class design leaders." },
  { icon: BookOpen, title: "Flagship masterclasses", desc: "Two full-day deep dives on brand at scale and conversion design." },
  { icon: Compass, title: "Quarterly brand audits", desc: "Four checkpoints a year, delivered as clear roadmaps." },
  { icon: Calendar, title: "Annual member summit", desc: "Members-only two closed-door days. Location announced per cohort." },
  { icon: Network, title: "Private member network", desc: "Curated peers. Non-competing, fast-scaling brands. Monthly roundtables." },
  { icon: Library, title: "Member library", desc: "Every session, framework and masterclass archived. Compounds year over year." },
];

const events = [
  { month: "FEB", year: "2026", type: "Leader Session", title: "Identity systems that survive scale", where: "Virtual" },
  { month: "APR", year: "2026", type: "Masterclass", title: "Brand building at scale — full-day", where: "Virtual" },
  { month: "MAY", year: "2026", type: "Leader Session", title: "The order stories are told in", where: "Virtual" },
  { month: "JUL", year: "2026", type: "Leader Session", title: "Voice & defensible tone", where: "Virtual" },
  { month: "SEP", year: "2026", type: "Masterclass", title: "Conversion-driven design — full-day", where: "Virtual" },
  { month: "NOV", year: "2026", type: "Annual Summit", title: "Two closed-door days · Keynotes, showcases, office hours", where: "Venue TBA", featured: true },
];

const capabilities = [
  "Brand identity", "Websites", "Ad creative", "Design systems", "Landing pages",
  "Motion & video", "Campaign systems", "Product design", "Guidelines & launch kits",
  "Messaging & voice", "Rebrands", "Presentations & decks",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo title="Midcreatives — A Global Creative Design Company" description="Access best-in-class creative talent, masterclasses with world design leaders, a network of the fastest-scaling brands, webinars and live events — and much more." path="/" organization={true} />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="glow-blue" />
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-8 md:pt-14 pb-16 md:pb-24 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="pill pill-outline mb-8">
              <span className="inline-block w-2 h-2 rounded-full bg-[color:var(--blue)]" />
              Cohort 2026 · Applications open
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display-tight text-[52px] sm:text-7xl lg:text-[128px] ink leading-[0.9] max-w-6xl"
            >
              We're Midcreatives. <span className="ink-muted">A global creative design company.</span>
            </h1>

            <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8">
              <p className="md:col-span-6 text-lg md:text-xl ink-muted leading-relaxed max-w-2xl">
                Access best-in-class creative talent, masterclasses with world design leaders, a network of the fastest-scaling brands, webinars &amp; live events — and much more.
              </p>
              <div className="md:col-span-6 flex flex-wrap items-center gap-3 md:justify-end">
                <Link to="/apply" data-testid="hero-apply-cta" className="btn-dark">
                  Get Membership <span className="arrow-slide">→</span>
                </Link>
                <Link to="/services" data-testid="hero-services-link" className="btn-light">
                  See our work
                </Link>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Marquee */}
        <div className="border-y border-white/10 overflow-hidden">
          <div className="flex marquee-track whitespace-nowrap py-4">
            {[...roster, ...roster].map((r, i) => (
              <span key={i} className="font-mono-mc mx-8">
                {r} <span className="ink-muted mx-8">✦</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* VALUE PROP */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pt-24 md:pt-40 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-3">
            <p className="font-mono-mc">(01) — Why we exist</p>
          </div>
          <div className="md:col-span-9">
            <h2 className="font-display-tight text-4xl sm:text-6xl lg:text-7xl ink leading-[1.0] max-w-5xl">
              We design brands people remember <span className="ink-muted">— and the digital experiences that keep them coming back.</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl">
              <p className="ink-muted leading-relaxed text-base md:text-lg">
                Every brand is becoming a digital experience. The identity people recognize, the ads
                they scroll past or stop for, the product they touch — it's all one experience now.
              </p>
              <p className="ink-muted leading-relaxed text-base md:text-lg">
                We exist to help ambitious brands design that experience deliberately — with senior
                craft on every touchpoint, and one team that stays with you all year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three pillar cards — Community · Talent · Events */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              img: "/membership-card.jpg",
              eyebrow: "Membership · Community",
              title: "A membership, not a subscription",
              body: "Join a private, invitation-based network of fast-scaling, non-competing brands. Monthly roundtables, leader AMAs, and peer access that compounds year over year.",
            },
            {
              img: "/talent-card.jpg",
              eyebrow: "Talent",
              title: "Best-in-class design talent",
              body: "Every deliverable is led by senior designers with creative direction on every piece. No juniors learning on your brand — the same team stays with you all year.",
            },
            {
              img: "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=1200",
              eyebrow: "Events",
              title: "Live events with design leaders",
              body: "Six live working sessions a year with world design leaders, two full-day flagship masterclasses, and one closed-door members-only annual summit.",
            },
          ].map((c, i) => (
            <Reveal key={c.eyebrow} delay={i * 0.08}>
              <article className="rounded-3xl border border-white/10 bg-[color:var(--surface)] overflow-hidden flex flex-col h-full hover:border-white/25 transition-colors duration-500 group">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={c.img}
                    alt={c.eyebrow}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="p-6 md:p-7 flex-1 flex flex-col">
                  <h3 className="font-display-tight text-2xl md:text-3xl ink leading-tight">
                    {c.title}
                  </h3>
                  <p className="mt-3 ink-muted text-sm leading-relaxed">
                    {c.body}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* WHAT WE DESIGN — 3 mockup cards inside 1 container */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-6 md:p-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8 md:mb-12">
            <div>
              <p className="font-mono-mc mb-4">(02) — What we design</p>
              <h2 className="font-display-tight text-4xl md:text-6xl ink leading-[1.0] max-w-3xl">
                Three practices. <span className="ink-muted">One team.</span>
              </h2>
            </div>
            <Link to="/services" className="btn-light self-start md:self-auto" data-testid="home-services-cta">
              Explore services <span className="arrow-slide">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="rounded-2xl border border-white/10 bg-[color:var(--surface-2)] overflow-hidden flex flex-col h-full hover:border-white/25 transition-colors duration-500">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img src={s.mockup} alt={s.title} className="w-full h-full object-cover" />
                    <div className="absolute top-4 left-4 inline-flex items-center px-3 py-1.5 rounded-full bg-black/55 backdrop-blur-md border border-white/10">
                      <p className="font-mono-mc text-white/85">{s.tag}</p>
                    </div>
                  </div>
                  <div className="p-6 md:p-8 flex-1 flex flex-col">
                    <h3 className="font-display-tight text-3xl md:text-4xl ink leading-[1.0]">{s.title}</h3>
                    <p className="mt-4 ink-muted leading-relaxed text-sm">{s.body}</p>
                    <ul className="mt-6 space-y-2">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-2 ink text-sm">
                          <Check size={14} className="mt-1 shrink-0 ink-muted" /> {it}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GLIMPSE OF WORK — masonry bento */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-8">
            <p className="font-mono-mc mb-4">(03) — Selected work</p>
            <h2 className="font-display-tight text-4xl md:text-6xl lg:text-7xl ink leading-[1.0]">
              A glimpse of work <span className="ink-muted">of Midcreatives designers.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:items-end">
            <p className="ink-muted leading-relaxed">
              Branding, campaigns, UI/UX and digital products — built under our creative direction.
            </p>
          <Link to="/work" className="btn-light mt-4 inline-flex items-center gap-2">
            View all work <span className="arrow-slide">→</span>
          </Link>
</div>
        </div>

        <div className="grid grid-cols-6 md:grid-cols-12 gap-3 md:gap-4">
          {glimpse.map((g, i) => (
            <Reveal key={i} delay={i * 0.05} className={g.span}>
              <div className={`relative rounded-2xl overflow-hidden border border-white/10 ${g.ratio} group cursor-pointer`}>
                <img src={g.url} alt={g.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <p className="font-mono-mc text-white/85">{g.label}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MEMBERSHIP — editorial split */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-8 md:p-16 relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
            {/* Left — pitch + CTA */}
            <div className="md:col-span-5">
              <p className="font-mono-mc mb-6">(04) — Our engagement model</p>
              <h2 className="font-display-tight text-4xl md:text-5xl lg:text-6xl ink leading-[1.0]">
                Everything, held in <span className="ink-muted">one membership.</span>
              </h2>
              <p className="ink-muted mt-6 leading-relaxed max-w-md">
                Membership isn't the point — the work is. This is the room we built to deliver
                it: a senior team, a big-project budget, and a network you can only be introduced
                into.
              </p>

              <div className="mt-10 space-y-1 max-w-sm">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="font-mono-mc">Commitment</span>
                  <span className="ink text-sm">Annual only</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="font-mono-mc">Entry</span>
                  <span className="ink text-sm">By application</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <span className="font-mono-mc">Cohort</span>
                  <span className="ink text-sm">Limited seats · 2026</span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="font-mono-mc">Member rate</span>
                  <span className="ink text-sm">15% off extra work</span>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap gap-3 items-center">
                <Link to="/apply" data-testid="home-pricing-cta" className="btn-dark">
                  Get Membership <span className="arrow-slide">→</span>
                </Link>
                <Link to="/membership" data-testid="home-pricing-details" className="ink font-medium link-ul">
                  See full details
                </Link>
              </div>
            </div>

            {/* Right — numbered editorial list */}
            <div className="md:col-span-7 md:pl-8 md:border-l md:border-white/10">
              <p className="font-mono-mc mb-8">Included in your membership</p>
              <ul>
                {membershipFeatures.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <Reveal key={f.title} delay={i * 0.04}>
                      <li className="group grid grid-cols-12 gap-4 md:gap-8 py-5 md:py-7 border-b border-white/10 items-start">
                        <span className="col-span-2 md:col-span-1 font-mono-mc pt-1">{String(i + 1).padStart(2, "0")}</span>
                        <div className="col-span-1 pt-1">
                          <Icon size={18} strokeWidth={1.5} className="ink-muted group-hover:ink transition-colors" />
                        </div>
                        <div className="col-span-9 md:col-span-7">
                          <h4 className="font-display-tight text-xl md:text-2xl ink leading-tight">{f.title}</h4>
                          <p className="ink-muted text-sm mt-1.5 leading-relaxed">{f.desc}</p>
                        </div>
                        <div className="hidden md:flex md:col-span-3 items-center justify-end pt-1">
                          <Check size={16} className="ink-muted group-hover:ink transition-colors" />
                        </div>
                      </li>
                    </Reveal>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERS TEASER */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-8 md:p-16 relative overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-8">
              <p className="font-mono-mc mb-4">(05) — Leaders</p>
              <h2 className="font-display-tight text-4xl md:text-6xl ink leading-[1.0]">
                Learn from the people your team already <span className="ink-muted">quotes.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/leaders" className="btn-light" data-testid="home-leaders-cta">
                Meet the leaders <span className="arrow-slide">→</span>
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              "https://images.unsplash.com/photo-1675726205553-4e348f24da2c?w=800",
              "https://images.unsplash.com/photo-1532170579297-281918c8ae72?w=800",
              "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800",
              "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=800",
            ].map((img, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="aspect-[4/5] rounded-2xl overflow-hidden border border-white/10">
                  <img src={img} alt="Design leader" className="w-full h-full object-cover grayscale hover:grayscale-0 hover:scale-105 transition-all duration-700" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EVENTS TEASER — 6-card premium calendar grid */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-8">
            <p className="font-mono-mc mb-4">(06) — Events · Cohort 2026 calendar</p>
            <h2 className="font-display-tight text-4xl md:text-6xl lg:text-7xl ink leading-[1.0]">
              Six sessions. Two masterclasses. <span className="ink-muted">One annual summit.</span>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:items-end md:justify-end">
            <Link to="/events" data-testid="home-events-cta" className="btn-light">
              See the calendar <span className="arrow-slide">→</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {events.map((e, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div
                className={`rounded-3xl p-6 md:p-8 h-full min-h-[260px] flex flex-col justify-between transition-all duration-500 ${
                  e.featured
                    ? "bg-cream-panel text-[#0a0a0a] hover:-translate-y-1"
                    : "bg-[color:var(--surface)] border border-white/10 hover:border-white/25"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`font-mono-mc ${e.featured ? "text-[#0a0a0a]/50" : ""}`}>{e.year}</p>
                    <p className={`font-display-tight text-6xl md:text-7xl leading-none mt-2 ${e.featured ? "text-[#0a0a0a]" : "ink"}`}>
                      {e.month}
                    </p>
                  </div>
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                      e.featured
                        ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                        : "border-white/15 ink-muted"
                    }`}
                  >
                    {e.type}
                  </span>
                </div>
                <div>
                  <h4 className={`font-display-tight text-xl md:text-2xl leading-tight ${e.featured ? "text-[#0a0a0a]" : "ink"}`}>
                    {e.title}
                  </h4>
                  <p className={`font-mono-mc mt-3 ${e.featured ? "text-[#0a0a0a]/60" : ""}`}>
                    {e.where}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="max-w-screen-2xl mx-auto px-6 md:px-10 lg:px-14 pb-24 md:pb-32">
        <div className="relative rounded-[32px] border border-white/10 bg-[color:var(--surface)] p-10 md:p-20 overflow-hidden">
          <div className="glow-blue" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="font-mono-mc mb-6">Apply for Cohort 2026</p>
              <h2 className="font-display-tight text-4xl md:text-7xl ink leading-[1.0]">
                Design your next decade <span className="ink-muted">deliberately.</span>
              </h2>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link to="/apply" data-testid="home-final-cta" className="btn-dark">
                Get Membership <span className="arrow-slide">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
